import { CameraUtils } from "../../utils/cameraUtils";
import "./viewControlExtension.scss";

export class ViewControlContext {
    public constructor(public forceLabels = false, public showLabelsOnZoom = true) {}
}

export interface ViewControlExtensionOptions {
    autodeskContainerId: string;
    showZoomButtons?: boolean;
    showRotateButtons?: boolean;
    showForceLabelsButton?: boolean;
    showLabelsOnZoomButton?: boolean;
    degreesPerClick?: number;
    viewControlContext: ViewControlContext;
}

export class ViewControlExtension extends Autodesk.Viewing.Extension {
    private floorToolBar: Autodesk.Viewing.UI.ToolBar;
    private subToolbar: Autodesk.Viewing.UI.ControlGroup | null;
    private maxPositionZ = 350;
    private minPositionZ = 5;
    private interval = 30;
    private rotation = 0;
    private viewControlContext: ViewControlContext;

    public constructor(viewer: Autodesk.Viewing.GuiViewer3D, public viewControlOptions: ViewControlExtensionOptions) {
        super(viewer, viewControlOptions);

        this.viewControlContext = viewControlOptions.viewControlContext;
    }

    public load(): boolean {
        return true;
    }

    public unload(): boolean {
        if (this.subToolbar) {
            this.viewer.toolbar.removeControl(this.subToolbar);

            if (this.floorToolBar.container) {
                this.viewer.container.removeChild(this.floorToolBar.container);
            }

            this.subToolbar = null;
        }

        return true;
    }

    public updateRotation(direction: number) {
        const rotationOffsetDegrees = this.viewControlOptions.degreesPerClick ?? 22.5;

        const newRotation = this.rotation + (rotationOffsetDegrees * direction);

        if (newRotation < 0) {
            this.rotation = newRotation + 360;
        } else if (newRotation > 360) {
            this.rotation = newRotation - 360;
        } else {
            this.rotation = newRotation;
        }
    }

    public onToolbarCreated(_toolbar: Autodesk.Viewing.UI.ToolBar): void {

        if (!this.viewControlOptions.showZoomButtons && !this.viewControlOptions.showRotateButtons && !this.viewControlOptions.showForceLabelsButton && !this.viewControlOptions.showLabelsOnZoomButton) {
            return;
        }

        if (this.floorToolBar == null) {
            this.floorToolBar = new Autodesk.Viewing.UI.ToolBar(`${this.viewControlOptions.autodeskContainerId}-zoom-toolbar`, {
                alignVertically: true,
            });
        }

        if (this.subToolbar == null) {
            this.subToolbar = new Autodesk.Viewing.UI.ControlGroup(`${this.viewControlOptions.autodeskContainerId}-zoom-subtoolbar`);

            if (this.viewControlOptions.showZoomButtons) {
                const zoomInButton = new ViewButton("zoom-in", "&nbsp;");
                const zoomOutButton = new ViewButton("zoom-out", "&nbsp;");

                zoomInButton.onClick = (): void => {
                    const camera: any = this.viewer.getCamera();

                    if (camera.position.z <= this.minPositionZ) {
                        return;
                    }

                    camera.position.z -= this.interval;

                    if (camera.position.z < this.minPositionZ) {
                        camera.position.z = this.minPositionZ;
                    }

                    this.viewer.navigation.setView(camera.position, camera.target);
                };

                zoomOutButton.onClick = (): void => {
                    const camera: any = this.viewer.getCamera();

                    if (camera.position.z >= this.maxPositionZ) {
                        return;
                    }

                    camera.position.z += this.interval;

                    if (camera.position.z > this.maxPositionZ) {
                        camera.position.z = this.maxPositionZ;
                    }

                    this.viewer.navigation.setView(camera.position, camera.target);
                };

                this.subToolbar.addControl(zoomInButton);
                this.subToolbar.addControl(zoomOutButton);
            }

            if (this.viewControlOptions.showRotateButtons) {
                const rotateLeftButton = new ViewButton("rotate-left", "&nbsp;");
                const rotateRightButton = new ViewButton("rotate-right", "&nbsp;");

                rotateLeftButton.onClick = (): void => {
                    this.updateRotation(-1);
                    CameraUtils.rotate(this.viewer, this.rotation);
                };

                rotateRightButton.onClick = (): void => {
                    this.updateRotation(1);
                    CameraUtils.rotate(this.viewer, this.rotation);
                };

                this.subToolbar.addControl(rotateLeftButton);
                this.subToolbar.addControl(rotateRightButton);
            }

            if (this.viewControlOptions.showForceLabelsButton && this.viewControlOptions.showLabelsOnZoomButton) {

                const menuButton = new Autodesk.Viewing.UI.ComboButton("labels-menu");
                menuButton.addClass("be-toolbar-button");

                const showLabelsOnZoomButton = this.createShowLabelsOnZoomButton(this.viewControlContext, this.viewer);
                const forceLabelsButton = this.createForceLabelsButton(this.viewControlContext, this.viewer, showLabelsOnZoomButton);

                menuButton.addControl(showLabelsOnZoomButton);
                menuButton.addControl(forceLabelsButton);

                this.subToolbar.addControl(menuButton);

            } else if (this.viewControlOptions.showForceLabelsButton) {

                const forceLabelsButton = this.createForceLabelsButton(this.viewControlContext, this.viewer);

                this.subToolbar.addControl(forceLabelsButton);
            }

            this.floorToolBar.addControl(this.subToolbar);
            this.viewer.container.appendChild(this.floorToolBar.container);
        }
    }

    private createShowLabelsOnZoomButton(viewControlContext: ViewControlContext, viewer: Autodesk.Viewing.GuiViewer3D): ViewButton {
        const localStorageshowLabelsOnZoom = localStorage.getItem("BeSense_LabelPreference_showLabelsOnZoom");
        if (localStorageshowLabelsOnZoom) {
            viewControlContext.showLabelsOnZoom = localStorageshowLabelsOnZoom === "true";
        }

        const toggleLabelsOnZoomButton = new ViewButton("toggle-labels-on-zoom", "&nbsp;");

        toggleLabelsOnZoomButton.onClick = (): void => {
            viewControlContext.showLabelsOnZoom = !viewControlContext.showLabelsOnZoom;

            localStorage.setItem("BeSense_LabelPreference_showLabelsOnZoom", viewControlContext.showLabelsOnZoom ? "true" : "false")

            this.addRemoveActiveClass(viewControlContext.showLabelsOnZoom, toggleLabelsOnZoomButton);

            // Force update
            const camera: any = viewer.getCamera();
            viewer.navigation.setView(camera.position, camera.target);
        }

        this.addRemoveActiveClass(viewControlContext.showLabelsOnZoom, toggleLabelsOnZoomButton);

        return toggleLabelsOnZoomButton;
    }

    private createForceLabelsButton(viewControlContext: ViewControlContext, viewer: Autodesk.Viewing.GuiViewer3D, showLabelsOnZoomButton?: ViewButton): ViewButton {

        const forceLabelsButton = new ViewButton(showLabelsOnZoomButton ? "force-labels-with-menu" : "force-labels", "&nbsp;");
        const localStorageForceLabels = localStorage.getItem("BeSense_LabelPreference_forceLabels");

        if (localStorageForceLabels) {
            viewControlContext.forceLabels = localStorageForceLabels === "true";
        }

        forceLabelsButton.onClick = (): void => {
            viewControlContext.forceLabels = !viewControlContext.forceLabels;

            localStorage.setItem("BeSense_LabelPreference_forceLabels", viewControlContext.forceLabels ? "true" : "false");

            // Turn of show labels on zoom when user doesn't want any labels
            if (showLabelsOnZoomButton && !viewControlContext.forceLabels) {
                viewControlContext.showLabelsOnZoom = false;
                localStorage.setItem("BeSense_LabelPreference_showLabelsOnZoom", "false");
                this.addRemoveActiveClass(viewControlContext.showLabelsOnZoom, showLabelsOnZoomButton);
            }

            this.addRemoveActiveClass(viewControlContext.forceLabels, forceLabelsButton);

            // Force update
            const camera: any = viewer.getCamera();
            viewer.navigation.setView(camera.position, camera.target);
        }

        this.addRemoveActiveClass(viewControlContext.forceLabels, forceLabelsButton);

        return forceLabelsButton;
    }

    private addRemoveActiveClass(value: boolean, button: ViewButton): void {
        if (value) {
            button.addClass("active");
        } else {
            button.removeClass("active");
        }
    }
}

class ViewButton extends Autodesk.Viewing.UI.Button {
    public constructor(id: string, label: string) {
        super(id);

        this.icon.innerHTML = label;
        this.addClass("be-toolbar-button");
    }
}
